import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import api from '../api/api';
import './Support.css';

const DEFAULT_DATA = {
  heroTitle: 'Support Centre',
  heroSubtitle: 'Everything you need — policies, warranty info, and ways to reach us.',
  ctaTitle: 'Still need help?',
  ctaSubtitle: 'Our team is available 7 days a week — 10:30am to 7:00pm.',
  ctaPhone: '0871002241',
  links: [
    { icon: '📋', title: 'Terms of Service',      desc: 'Read our full terms and conditions.',                         href: '/support/tos',           external: false },
    { icon: '🛡️', title: 'Warranty Policy',        desc: 'Understand what is covered under our product warranty.',     href: '/support/warranty',      external: false },
    { icon: '🔒', title: 'Privacy Policy',         desc: 'Learn how we collect, use, and protect your personal data.', href: '/support/privacy',       external: false },
    { icon: '↩️', title: 'Return & Refund Policy', desc: 'Find out how to return a product and our refund timelines.', href: '/support/return-refund', external: false },
    { icon: '💬', title: 'Contact Us',             desc: 'Have a question or need help? Get in touch with our team.',  href: '/contact',               external: false },
  ],
};

// Policy detail page — shown at /support/:policy
export function PolicyPage() {
  const { policy } = useParams();
  const navigate = useNavigate();
  const [sections, setSections] = useState([]);
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(true);

  const POLICY_META = {
    'tos':           { icon: '📋', title: 'Terms of Service' },
    'warranty':      { icon: '🛡️', title: 'Warranty Policy' },
    'privacy':       { icon: '🔒', title: 'Privacy Policy' },
    'return-refund': { icon: '↩️', title: 'Return & Refund Policy' },
  };

  useEffect(() => {
    const meta = POLICY_META[policy];
    if (!meta) { navigate('/support', { replace: true }); return; }
    setTitle(meta.title);
    api.get(`/content/policy_${policy}`)
      .then(({ data }) => setSections(Array.isArray(data?.sections) ? data.sections : []))
      .catch(() => setSections([]))
      .finally(() => setLoading(false));
  }, [policy]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="policy-page">
      <div className="policy-hero">
        <div className="container">
          <h1>{POLICY_META[policy]?.icon} {title}</h1>
        </div>
      </div>
      <div className="container">
        <div className="policy-body">
          {loading ? (
            <p style={{ color: '#888', padding: '40px 0', textAlign: 'center' }}>Loading...</p>
          ) : sections.length > 0 ? (
            <div className="policy-content">
              {sections.map((sec, i) => (
                <div key={i} className="policy-section" style={{ textAlign: sec.align || 'left' }}>
                  {sec.heading && <h2>{sec.heading}</h2>}
                  {(sec.blocks || []).map((block, bi) => (
                    block.type === 'list' ? (
                      <ul key={bi} className="policy-list">
                        {(block.items || []).filter(item => item.trim()).map((item, ii) => (
                          <li key={ii}>{item}</li>
                        ))}
                      </ul>
                    ) : (
                      block.value?.trim() ? <p key={bi}>{block.value}</p> : null
                    )
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <div className="policy-empty">
              <p>This page has no content yet.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Main support landing page
function Support() {
  const [data, setData] = useState(DEFAULT_DATA);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/support').then(({ data: d }) => {
      if (d) setData({ ...DEFAULT_DATA, ...d, links: d.links?.length ? d.links : DEFAULT_DATA.links });
    }).catch(() => {});
  }, []);

  const handleCardClick = (item) => {
    if (item.external) {
      window.open(item.href, '_blank', 'noopener,noreferrer');
    } else {
      navigate(item.href);
    }
  };

  return (
    <div className="support-page">

      <div className="support-hero">
        <div className="container">
          <div className="support-hero-icon">🤝</div>
          <h1>{data.heroTitle}</h1>
          <p>{data.heroSubtitle}</p>
        </div>
      </div>

      <div className="container">
        <div className="support-grid">
          {data.links.map((item, i) => (
            <button key={i} className="support-card" onClick={() => handleCardClick(item)}>
              <div className="support-card-icon">{item.icon}</div>
              <div className="support-card-body">
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
              <span className="support-card-arrow">→</span>
            </button>
          ))}
        </div>
      </div>

      <div className="support-cta">
        <div className="container">
          <h2>{data.ctaTitle}</h2>
          <p>{data.ctaSubtitle}</p>
          <div className="support-cta-btns">
            <a href={`tel:${data.ctaPhone}`} className="btn">📞 Call Us</a>
            <Link to="/contact" className="btn support-btn-outline">✉️ Send a Message</Link>
          </div>
        </div>
      </div>

    </div>
  );
}

export default Support;
